/**
 * @fileoverview Appointment-type-scoped role titles (MOR-2354, under
 * MOR-2342).
 *
 * The signatory and practitioner titles beneath a signature vary by
 * appointment type ("Liquidator", "Voluntary Administrator", "Bankruptcy
 * Trustee" …) and are derived from the job. A firm may fix a house style for
 * ONE type — WLP signs bankruptcy letters as "Trustee" — without touching any
 * other type. Such an override is stored on the Practitioner preset under a
 * key scoped to the appointment type, e.g. `signatory_title:personal_bankruptcy`.
 *
 * A legacy UNSCOPED `signatory_title` / `practitioner_title` preset value is
 * kept in storage but is never applied to any job; the settings page shows it
 * and asks the firm to assign it to the types it was meant for, or discard it.
 */

import { APPOINTMENT_TYPES, type AppointmentType } from "@cvx/jobs/schema";

/** The two role-title fields a firm may fix per appointment type. */
export const TITLE_KEYS = ["signatory_title", "practitioner_title"] as const;
export type TitleKey = (typeof TITLE_KEYS)[number];

/** Every appointment type, in schema order. */
export const TITLE_APPOINTMENT_TYPES: ReadonlyArray<AppointmentType> =
  Object.values(APPOINTMENT_TYPES);

const SCOPE_SEPARATOR = ":";

/** `signatory_title:personal_bankruptcy`. */
export function scopedTitleKey(
  titleKey: TitleKey,
  appointmentType: string,
): string {
  return `${titleKey}${SCOPE_SEPARATOR}${appointmentType}`;
}

/**
 * Decode a scoped key, or null for anything else (including the legacy
 * unscoped title keys and unknown appointment types).
 */
export function parseScopedTitleKey(
  key: string,
): { titleKey: TitleKey; appointmentType: AppointmentType } | null {
  const idx = key.indexOf(SCOPE_SEPARATOR);
  if (idx === -1) return null;
  const titleKey = key.slice(0, idx);
  const appointmentType = key.slice(idx + 1);
  if (!(TITLE_KEYS as ReadonlyArray<string>).includes(titleKey)) return null;
  if (
    !(TITLE_APPOINTMENT_TYPES as ReadonlyArray<string>).includes(
      appointmentType,
    )
  ) {
    return null;
  }
  return {
    titleKey: titleKey as TitleKey,
    appointmentType: appointmentType as AppointmentType,
  };
}

/** All scoped title keys a Practitioner preset may hold. */
export const SCOPED_TITLE_KEYS: ReadonlyArray<string> = TITLE_KEYS.flatMap(
  (titleKey) =>
    TITLE_APPOINTMENT_TYPES.map((type) => scopedTitleKey(titleKey, type)),
);

/** True for a scoped key or a legacy unscoped title key. */
export function isTitleConfigKey(key: string): boolean {
  return (
    (TITLE_KEYS as ReadonlyArray<string>).includes(key) ||
    parseScopedTitleKey(key) !== null
  );
}

/**
 * The firm's title overrides that apply to THIS appointment type: non-empty
 * scoped values only. Nothing applies when the job has no (or an unknown)
 * appointment type — a type-scoped house style cannot be matched to it.
 *
 * @example
 * resolveScopedTitles({ "signatory_title:personal_bankruptcy": "Trustee" }, "personal_bankruptcy");
 * // { signatory_title: "Trustee" }
 * resolveScopedTitles({ "signatory_title:personal_bankruptcy": "Trustee" }, "cvl"); // {}
 */
export function resolveScopedTitles(
  presetValues: Readonly<Record<string, string>>,
  appointmentType: string | null | undefined,
): Partial<Record<TitleKey, string>> {
  if (!appointmentType) return {};
  return Object.fromEntries(
    TITLE_KEYS.flatMap((titleKey) => {
      const value = presetValues[scopedTitleKey(titleKey, appointmentType)];
      return value && value.trim() ? [[titleKey, value.trim()]] : [];
    }),
  );
}

/** The values map without any title configuration keys (scoped or legacy). */
export function withoutTitleConfigKeys(
  values: Readonly<Record<string, string>>,
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(values).filter(([key]) => !isTitleConfigKey(key)),
  );
}

/**
 * Legacy unscoped title values still stored on a preset. They are not applied
 * anywhere; the settings page surfaces them for an explicit decision.
 */
export function legacyUnscopedTitles(
  presetValues: Readonly<Record<string, string>>,
): Partial<Record<TitleKey, string>> {
  return Object.fromEntries(
    TITLE_KEYS.flatMap((titleKey) => {
      const value = presetValues[titleKey];
      return value && value.trim() ? [[titleKey, value.trim()]] : [];
    }),
  );
}
